// P-ONBOARD-A — Behavior-anchored parent abilities survey (MATH half).
//
// Second of the two screens that together cover the anchored-priors survey
// (see onboarding.kid.abilities.tsx for the layout / response-semantics
// rationale). Reading questions are answered first; this screen continues
// with the 5 math questions and then commits to /onboarding/kid/handoff.
//
// kidStepProgress("abilities") returns the SAME "Step N of M" counter on
// both screens (one logical step, two physical screens) so the counter
// doesn't bump mid-survey. Sub-label distinguishes Reading vs Math.

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useOnboardingDraft } from "@/lib/onboardingDraft";
import { kidStepProgress } from "@/lib/kidOnboarding";
import {
  ABILITY_OPTIONS,
  responseToPrior,
  confidenceFor,
} from "@/lib/engine/parentPriors";
import type { ParentPriors, AbilityResponse } from "@/App/progress";

export const Route = createFileRoute("/onboarding/kid/abilities-math")({
  head: () => ({ meta: [{ title: "PencilWise · Math check-in" }] }),
  component: KidAbilitiesMath,
});

const RESPONSES: { value: AbilityResponse; label: string }[] = [
  { value: "usually", label: "Usually on their own" },
  { value: "sometimes", label: "Sometimes with help" },
  { value: "not_yet", label: "Not yet" },
  { value: "unsure", label: "Not sure" },
];

// H-A4 shared step styles (H-0 tokens) — mirrors onboarding.kid.abilities.tsx
// exactly so the two halves of the survey read as one surface.
const EYEBROW = "text-xs font-bold uppercase tracking-[0.18em] text-terracotta-deep";
const CTA =
  "flex min-h-[52px] scroll-mb-28 items-center justify-center rounded-2xl bg-primary px-6 text-base font-extrabold text-primary-foreground shadow-press transition-all active:translate-y-0.5 active:bg-primary-pressed active:shadow-none disabled:opacity-50";

function KidAbilitiesMath() {
  const navigate = useNavigate();
  const { draft, setDraft } = useOnboardingDraft();
  const { current, total } = kidStepProgress("abilities");
  const name = draft.name?.trim() || "your child";

  const mathSlugs = ABILITY_OPTIONS.filter((o) => o.group === "math");
  const [picked, setPicked] = useState<Record<string, AbilityResponse>>(() => {
    const out: Record<string, AbilityResponse> = {};
    for (const opt of mathSlugs) {
      const c = confidenceFor(draft.parentPriors, opt.slug);
      if (c) out[opt.slug] = c;
    }
    return out;
  });

  const choose = (slug: string, response: AbilityResponse) =>
    setPicked((prev) => ({ ...prev, [slug]: response }));

  const goBack = () =>
    navigate({ to: "/onboarding/kid/abilities", search: (prev) => prev });

  const submit = () => {
    // Merge math answers into the EXISTING draft.parentPriors (reading
    // answers from the first screen must persist through this commit).
    const next: ParentPriors = { ...(draft.parentPriors ?? {}) };
    for (const opt of mathSlugs) {
      const response = picked[opt.slug];
      if (!response) {
        delete next[opt.slug];
        delete next[`${opt.slug}_confidence`];
        continue;
      }
      const entry = responseToPrior(response);
      next[opt.slug] = entry.value;
      next[`${opt.slug}_confidence`] = entry.confidence;
    }
    setDraft({ parentPriors: next });
    navigate({ to: "/onboarding/kid/handoff", search: (prev) => prev });
  };

  return (
    <div className="relative min-h-screen bg-cream paper-grain">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-10">
        <header className="pointer-events-none animate-slide-up">
          <p className={EYEBROW}>
            Step {current} of {total} · Math
          </p>
          <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight">
            What can {name} do with numbers?
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Same idea — pick what fits best. &quot;Not sure&quot; is fine.
          </p>
        </header>

        <div className="mt-6 space-y-4">
          {mathSlugs.map((o) => (
            <fieldset
              key={o.slug}
              data-slug={o.slug}
              className="rounded-2xl border border-border/60 bg-card p-4 shadow-soft"
            >
              <legend className="float-left text-sm font-bold leading-snug text-foreground">
                {o.label}
              </legend>
              <div
                className="clear-both grid grid-cols-1 gap-2 pt-3"
                role="radiogroup"
                aria-label={o.label}
              >
                {RESPONSES.map((r) => {
                  const on = picked[o.slug] === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      data-response={r.value}
                      onClick={() => choose(o.slug, r.value)}
                      className={`flex min-h-[48px] w-full items-center gap-3 rounded-xl border-2 px-4 py-2 text-left text-sm font-bold transition-colors ${
                        on
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border bg-background text-foreground hover:border-primary"
                      }`}
                    >
                      <span
                        className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 text-xs ${
                          on
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-ink/40 bg-transparent"
                        }`}
                        aria-hidden="true"
                      >
                        {on ? "✓" : ""}
                      </span>
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>

        <div className="mt-6 flex gap-2">
          <button
            onClick={goBack}
            className="flex min-h-[52px] flex-1 scroll-mb-28 items-center justify-center rounded-2xl border-2 border-border bg-card px-4 text-sm font-bold text-muted-foreground shadow-soft transition-colors hover:border-primary"
          >
            ← Back
          </button>
          <button onClick={submit} className={`flex-[2] ${CTA}`}>
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}
